export type AppointmentTimeTagInput = {
  start_at?: string | null
  end_at?: string | null
  flex_type?: string | null
  timeframe_start_at?: string | null
  timeframe_end_at?: string | null
  customer_window_start_at?: string | null
  customer_window_end_at?: string | null
  estimated_duration_min?: number | string | null
  duration_minutes?: number | string | null
}

export type AppointmentTimeTagMode = "customer" | "calendar"

export type AppointmentTimeTagPresentation = {
  label: string
  baseLabel: string
  durationLabel: string | null
  isFlex: boolean
  usesCustomerWindow: boolean
  customerWindowLabel: string | null
  timeWindowLabel: string | null
  scheduledPeriodLabel: string | null
  tooltipDescription: string | null
}

const parseDate = (value?: string | null) => {
  if (!value) return null

  const parsed = new Date(value)

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const formatDate = (value: Date, options?: Intl.DateTimeFormatOptions) =>
  value.toLocaleDateString("de-DE", {
    timeZone: "Europe/Berlin",
    ...options,
  })

const formatTime = (value: Date) =>
  value.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Berlin",
  })

const formatHeadingDate = (value: Date) =>
  formatDate(value, {
    day: "numeric",
    month: "numeric",
  })

const headingTimeFormat = new Intl.DateTimeFormat("de-DE", {
  hour: "numeric",
  minute: "2-digit",
  timeZone: "Europe/Berlin",
})

const formatHeadingTime = (value: Date) => {
  const parts = headingTimeFormat.formatToParts(value)
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((entry) => entry.type === type)?.value ?? ""
  const hour = part("hour")
  const minute = part("minute")

  return minute === "00" ? hour : `${hour}:${minute}`
}

const normalizeDurationMinutes = (value?: number | string | null) => {
  if (value === null || value === undefined || value === "") return null

  const minutes = typeof value === "number" ? value : Number(`${value}`.trim())

  if (!Number.isFinite(minutes) || minutes <= 0) return null

  return Math.round(minutes)
}

export const formatDurationMinutes = (minutes?: number | null) => {
  if (!minutes || !Number.isFinite(minutes) || minutes <= 0) return null

  if (minutes < 60) return `${minutes}min`

  const hours = Math.floor(minutes / 60)
  const remaining = minutes % 60

  return remaining ? `${hours}h ${remaining}min` : `${hours}h`
}

export const formatAppointmentDuration = (startAt?: string | null, endAt?: string | null) => {
  const start = parseDate(startAt)
  const end = parseDate(endAt)

  if (!start || !end) return null

  const minutes = Math.round((end.getTime() - start.getTime()) / 60000)

  return formatDurationMinutes(minutes)
}

const formatShortDate = (value: Date) =>
  formatDate(value, {
    day: "2-digit",
    month: "2-digit",
  })

const formatLongDate = (value: Date) => formatDate(value)

const sameLocalDay = (left: Date, right: Date) => formatLongDate(left) === formatLongDate(right)

const formatCompactWindow = (start: Date, end?: Date | null) => {
  if (!end) return `${formatShortDate(start)} ${formatTime(start)}`

  if (sameLocalDay(start, end)) {
    return `${formatShortDate(start)} ${formatTime(start)}-${formatTime(end)}`
  }

  return `${formatShortDate(start)} ${formatTime(start)}-${formatShortDate(end)} ${formatTime(end)}`
}

const formatTooltipWindow = (start?: Date | null, end?: Date | null) => {
  if (!start) return null

  if (!end) return `${formatLongDate(start)}, ${formatTime(start)} Uhr`

  if (sameLocalDay(start, end)) {
    return `${formatLongDate(start)}, ${formatTime(start)}-${formatTime(end)} Uhr`
  }

  return `${formatLongDate(start)}, ${formatTime(start)} Uhr bis ${formatLongDate(end)}, ${formatTime(end)} Uhr`
}

export const isFlexAppointmentTime = (value?: AppointmentTimeTagInput | null) =>
  `${value?.flex_type ?? ""}`.toUpperCase() === "SOFT"

export const formatAppointmentHeadingWindow = (
  value?: AppointmentTimeTagInput | null,
): string | null => {
  const isFlex = isFlexAppointmentTime(value)
  const start =
    parseDate(value?.customer_window_start_at) ??
    (isFlex ? parseDate(value?.timeframe_start_at) : null) ??
    parseDate(value?.start_at)
  const end =
    parseDate(value?.customer_window_end_at) ??
    (isFlex ? parseDate(value?.timeframe_end_at) : null) ??
    parseDate(value?.end_at)

  if (!start || !end) return null

  return `${formatHeadingDate(start)} (${formatHeadingTime(start)}–${formatHeadingTime(end)} Uhr)`
}

const durationMinutesLabel = (value?: number | string | null) =>
  formatDurationMinutes(normalizeDurationMinutes(value))

export const buildAppointmentTimeTagPresentation = (
  value?: AppointmentTimeTagInput | null,
  options?: { mode?: AppointmentTimeTagMode },
): AppointmentTimeTagPresentation => {
  const mode = options?.mode ?? "customer"
  const isFlex = isFlexAppointmentTime(value)
  const start = parseDate(value?.start_at)
  const end = parseDate(value?.end_at)
  const flexWindowStart = parseDate(value?.timeframe_start_at) ?? start
  const flexWindowEnd = parseDate(value?.timeframe_end_at) ?? end
  const customerWindowStart = parseDate(value?.customer_window_start_at)
  const customerWindowEnd = parseDate(value?.customer_window_end_at)
  const usesCustomerWindow = mode === "customer" && Boolean(customerWindowStart)
  const windowStart = usesCustomerWindow ? customerWindowStart : isFlex ? flexWindowStart : start
  const windowEnd = usesCustomerWindow ? customerWindowEnd : isFlex ? flexWindowEnd : null
  const labelStart = windowStart ?? start

  if (!labelStart) {
    return {
      label: "Kein Termin",
      baseLabel: "Kein Termin",
      durationLabel: null,
      isFlex,
      usesCustomerWindow: false,
      customerWindowLabel: null,
      timeWindowLabel: null,
      scheduledPeriodLabel: null,
      tooltipDescription: null,
    }
  }

  const scheduledPeriodDurationLabel = formatAppointmentDuration(value?.start_at, value?.end_at)
  const durationLabel =
    durationMinutesLabel(value?.estimated_duration_min) ??
    durationMinutesLabel(value?.duration_minutes) ??
    scheduledPeriodDurationLabel ??
    null
  const baseLabel = windowEnd
    ? formatCompactWindow(labelStart, windowEnd)
    : formatCompactWindow(labelStart)
  const customerWindowLabel = formatTooltipWindow(customerWindowStart, customerWindowEnd)

  return {
    label: durationLabel ? `${baseLabel} (${durationLabel})` : baseLabel,
    baseLabel,
    durationLabel,
    isFlex,
    usesCustomerWindow,
    customerWindowLabel,
    timeWindowLabel: usesCustomerWindow
      ? customerWindowLabel
      : isFlex
        ? formatTooltipWindow(windowStart, windowEnd)
        : null,
    scheduledPeriodLabel: formatTooltipWindow(start, end),
    tooltipDescription: usesCustomerWindow
      ? "Das Zeitfenster für den Kunden enthält zusätzlichen Puffer und wird auf volle Zeitabstände gerundet. Die Kalenderzeit bleibt unverändert."
      : isFlex
        ? "Die Arbeit kann innerhalb des angegebenen Zeitfensters eingeplant werden."
        : null,
  }
}
